import { useState, useEffect } from 'react';
import { Save, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { API, axios, showToast } from './adminHelpers';

export const SEOManager = ({ authHeader }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/settings`, { headers: authHeader });
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [authHeader]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, { headers: authHeader });
      showToast('SEO settings saved!');
    } catch {
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateSEO = (field, value) => {
    setSettings(prev => ({ ...prev, seo: { ...prev?.seo, [field]: value } }));
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="seo-manager">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">SEO Settings</h1>
        <Button onClick={saveSettings} disabled={saving} className="bg-primary hover:bg-primary-hover" data-testid="save-seo-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold mb-4">General SEO</h2>
          <div className="space-y-4">
            <div>
              <Label>Site Title</Label>
              <Input value={settings?.seo?.site_title || ''} onChange={(e) => updateSEO('site_title', e.target.value)} className="mt-1" data-testid="seo-site-title" />
            </div>
            <div>
              <Label>Site Description</Label>
              <Textarea value={settings?.seo?.site_description || ''} onChange={(e) => updateSEO('site_description', e.target.value)} rows={3} className="mt-1" data-testid="seo-site-description" />
            </div>
            <div>
              <Label>Keywords</Label>
              <Input value={settings?.seo?.site_keywords || ''} onChange={(e) => updateSEO('site_keywords', e.target.value)} className="mt-1" data-testid="seo-keywords" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold mb-4">Robots.txt</h2>
          <Textarea value={settings?.seo?.robots_txt || ''} onChange={(e) => updateSEO('robots_txt', e.target.value)} rows={8} className="font-mono text-sm" data-testid="seo-robots-txt" />
          <p className="text-xs text-gray-500 mt-2">
            Live at: <a href="https://13cabsbook.com.au/api/robots.txt" target="_blank" rel="noreferrer" className="text-primary">https://13cabsbook.com.au/api/robots.txt</a>
          </p>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="font-semibold mb-4">Sitemap</h2>
        <p className="text-gray-600 mb-2">Your sitemap is automatically generated and includes all pages.</p>
        <a href="https://13cabsbook.com.au/api/sitemap.xml" target="_blank" rel="noreferrer" className="text-primary hover:underline">
          https://13cabsbook.com.au/api/sitemap.xml
        </a>
        <p className="text-xs text-gray-500 mt-2">Submit this URL to Google Search Console for indexing.</p>
      </div>
    </div>
  );
};
